import React, { useEffect, useState } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, 
  Chip, IconButton, Tooltip, CircularProgress, Stack, Alert
} from '@mui/material';
import { Edit, Delete, Refresh } from '@mui/icons-material';
import { getAllMasterForms, deleteMasterForm } from './FormManagementQueries';

const MasterFormsTable = ({ onEdit, onRefresh }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const loadForms = async () => {
    setLoading(true);
    try {
      const result = await getAllMasterForms();
      const formsList = result?.forms || result?.Items || [];
      setForms(formsList);
    } catch (error) {
      console.error('Error loading master forms:', error);
      setAlert({ open: true, message: 'Failed to load forms', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const handleDelete = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;
    
    try {
      await deleteMasterForm(formId);
      setAlert({ open: true, message: 'Form deleted successfully', severity: 'success' });
      loadForms();
      if (onRefresh) onRefresh();
    } catch (error) {
      setAlert({ open: true, message: 'Failed to delete form', severity: 'error' });
    }
  };

  const getFormTypeChip = (type) => {
    const colors = {
      'annual': 'primary',
      'probation': 'secondary',
    };
    return <Chip label={type} color={colors[type] || 'default'} size="small" />;
  };

  const getStatusChip = (status) => {
    const colors = {
      'draft': 'default',
      'active': 'success',
      'closed': 'error',
      'archived': 'warning',
    };
    return <Chip label={status || 'draft'} color={colors[status] || 'default'} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Get ordinal suffix (st, nd, rd, th)
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    
    return `${month} ${day}${getOrdinal(day)} ${year}`;
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Master Forms</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadForms} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {alert.open && (
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : forms.length === 0 ? (
        <Box py={4} display="flex" justifyContent="center">
          <Typography variant="body2" color="text.secondary">No master forms found</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Form Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map((form) => (
              <TableRow key={form.id} hover>
                <TableCell>{form.form_name || 'Untitled Form'}</TableCell>
                <TableCell>{getFormTypeChip(form.form_type)}</TableCell>
                <TableCell>{formatDate(form.start_date)}</TableCell>
                <TableCell>{formatDate(form.end_date)}</TableCell>
                <TableCell>{getStatusChip(form.status)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit Form">
                      <IconButton size="small" color="primary" onClick={() => onEdit(form)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Form">
                      <IconButton size="small" color="error" onClick={() => handleDelete(form.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default MasterFormsTable;
